// The page's handle on the review scan: opens and closes it, keeps it tied to
// the live engine, pauses it for an armed tool, and owns Space and Escape.
// Stays in the main bundle; the scan itself (controller, bar, overlay) is one
// chunk fetched the first time the button is hovered or pressed.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ViewportEngine } from '../../viewer/ViewportEngine';
import type { PlacedField } from '../../viewer/scan/coverage';
import type { DrawnPlan } from '../../viewer/scan/drawnPlan';
import type { ReviewScan } from '../../viewer/scan/ReviewScan';
import type { ScanArea, ScanSession, TissueStatus } from '../../viewer/scan/session';
import type { TissuePlan } from '../../viewer/scan/tissuePlan';
import type { AnnotationTool } from './AnnotationOverlay';

type ScanModule = typeof import('../viewer-controls/reviewScanModule');
let chunk: Promise<ScanModule> | null = null;
/** The chunk once it has landed: what an open scan can call without waiting. */
let loaded: ScanModule | null = null;
/** The scan's chunk, fetched once; a failed fetch is forgotten so the next
 *  press tries again (a network blip, or a deploy swapping the chunks). */
export function loadReviewScan(): Promise<ScanModule> {
  chunk ??= import('../viewer-controls/reviewScanModule')
    .then((m) => (loaded = m))
    .catch((e: unknown) => {
      chunk = null;
      throw e;
    });
  return chunk;
}

export const TOOL_BLOCKS = 'Leave the tool to continue (Esc)';
export const DRAW_BLOCKS = 'Draw an area on the image first';
export const NO_TISSUE_IN_AREAS = 'No tissue in the drawn areas';
/** A moved sensitivity finds the tissue again this long after its last move. */
export const SENSITIVITY_SETTLE_MS = 200;
/** In the sensitivity's popover when the last move found nothing to use. */
export const SENSITIVITY_NOTES = {
  none: 'No tissue at this sensitivity',
  all: 'All of the image counts as tissue at this sensitivity',
  /** The image could not be read, or the detector failed: not the setting. */
  failed: 'Could not look for the tissue, move the slider to try again',
} as const;

/** The note for a sensitivity that found nothing to use, by why. */
function sensitivityNote(reason?: string): string {
  if (reason === 'all-tissue') return SENSITIVITY_NOTES.all;
  if (reason === 'no-tissue' || reason === 'dark-background') return SENSITIVITY_NOTES.none;
  return SENSITIVITY_NOTES.failed;
}

export interface UseReviewScanOptions {
  engine: ViewportEngine | null;
  /** A lens (whole-slide) engine in single-slide mode. */
  supported: boolean;
  tool: AnnotationTool;
  /** The slide on screen: tissue detection is cached by it. */
  seriesInstanceUID?: string;
  /** The image's public id when the reader's seen areas are kept (signed in,
   *  never through a share link): the scan's coverage is then theirs, loaded
   *  when it opens and saved as it grows (viewer/seen). */
  seenImageId?: string | null;
  /** Test seam: builds the controller instead of the chunk. */
  create?: (engine: ViewportEngine, seenImageId: string | null) => ReviewScan | Promise<ReviewScan>;
  /** Test seam: finds the tissue instead of the chunk. */
  findTissue?: (engine: ViewportEngine, seriesInstanceUID: string, sensitivity: number) => Promise<TissuePlan>;
  /** Test seam: plans the drawn areas instead of the chunk. */
  drawnPlan?: (areas: PlacedField[]) => DrawnPlan;
  /** Test seam: lets go of what the detector kept, one slide's raster, or
   *  (no slide) everything, instead of the chunk. */
  forgetTissue?: (seriesInstanceUID?: string) => void;
}

export interface UseReviewScan {
  /** Non-null while the bar is open. */
  session: ScanSession | null;
  /** The scan's code is on its way after a press: the bar is already shown,
   *  analyzing. */
  pending: boolean;
  open: boolean;
  /** The chunk is on its way after a press. */
  loading: boolean;
  toggle: () => void;
  close: () => void;
  /** Start fetching the chunk so the press is instant. */
  prefetch: () => void;
}

/** Whether Space pressed with this element focused activates it natively, so
 *  the scan must leave the key alone. */
export function spaceActivates(el: Element | null): boolean {
  return !!el?.closest(
    'button, a[href], input, textarea, select, summary, [contenteditable="true"], ' +
      '[role="button"], [role="switch"], [role="checkbox"], [role="menuitem"], [role="menuitemradio"], [role="option"], [role="tab"]',
  );
}

const NO_TISSUE_YET: TissueStatus = { state: 'idle' };

/** What the detector kept, let go of: a slide's raster, or everything. Only
 *  once the chunk is in: before that nothing was kept. */
function forgetKept(seriesInstanceUID?: string): void {
  if (seriesInstanceUID === undefined) loaded?.invalidateTissue();
  else loaded?.releaseRasters(seriesInstanceUID);
}

export function useReviewScan({
  engine,
  supported,
  tool,
  seriesInstanceUID,
  seenImageId = null,
  create,
  findTissue,
  drawnPlan,
  forgetTissue = forgetKept,
}: UseReviewScanOptions): UseReviewScan {
  const [scan, setScan] = useState<ReviewScan | null>(null);
  // The bar's "Show visited areas": off when the scan opens or restarts and
  // once it plays, and the reader's to flip in between.
  const [onImage, setOnImage] = useState(false);
  const [loading, setLoading] = useState(false);
  // Where the scan goes; the ref is what a replan reads.
  const [area, setAreaState] = useState<ScanArea>('whole');
  const areaRef = useRef<ScanArea>('whole');
  // Tissue only: the reader's switch (on unless they turn it off) and what is
  // applied (only once the tissue is known, so off until then).
  const wantTissue = useRef(true);
  const [tissueOnly, setTissueOnlyState] = useState(false);
  const [sensitivity, setSensitivityState] = useState(0);
  const [tissue, setTissue] = useState<TissueStatus>(NO_TISSUE_YET);
  const plan = useRef<TissuePlan | null>(null);
  // Tissue only inside drawn areas that hold none: nothing to scan.
  const [emptyClip, setEmptyClip] = useState(false);
  // The areas drawn to scan (area `draw`): the ref is what a replan reads,
  // the state what the bar and the layer show.
  const [areas, setAreasState] = useState<PlacedField[]>([]);
  const areasRef = useRef<PlacedField[]>([]);
  const setAreas = useCallback((next: PlacedField[]) => {
    areasRef.current = next;
    setAreasState(next);
  }, []);
  // Once the reader flips Tissue only, a late detection no longer flips it for them.
  const chosen = useRef(false);
  // Whether this scan has had its first answer about the tissue: later ones
  // come from moving the sensitivity.
  const found = useRef(false);
  // The sensitivity the plan in hand was found at: a slider that comes back
  // to it has nothing to find, and must not replan (a replan pauses a
  // playing scan and starts its path over).
  const planned = useRef(0);
  const scanRef = useRef<ReviewScan | null>(null);
  // A press that is still fetching is cancelled by a close or a later press.
  const opening = useRef(0);
  // The engine the open (or opening) scan drives. A scan is bound to it for
  // life: another slide, or compare, means another engine and the end of it.
  const forEngine = useRef<ViewportEngine | null>(null);

  const close = useCallback(() => {
    opening.current++;
    forEngine.current = null;
    setLoading(false);
    const s = scanRef.current;
    scanRef.current = null;
    s?.dispose();
    setScan(null);
    setAreaState('whole');
    areaRef.current = 'whole';
    wantTissue.current = true;
    setTissueOnlyState(false);
    setSensitivityState(0);
    setEmptyClip(false);
    setTissue(NO_TISSUE_YET);
    plan.current = null;
    chosen.current = false;
    found.current = false;
    planned.current = 0;
    // Drawn areas go with the scan (the user, 2026-10-01).
    setAreas([]);
  }, [setAreas]);

  /** Plan what is scanned: the entire image or the drawn areas, kept to the
   *  tissue there when Tissue only is on and the tissue is known (the user,
   *  2026-10-01: "it would scan the intersection of the two"). The
   *  controller replans at once before Play, and from the start (coverage
   *  kept) once a scan is under way. */
  const apply = useCallback(
    (s: ReviewScan) => {
      const p = plan.current;
      const ok = p?.ok ? p : null;
      const onTissue = wantTissue.current && !!ok;
      let empty = false;
      if (areaRef.current === 'draw') {
        const drawn = areasRef.current;
        if (ok && onTissue && drawn.length) {
          const within = ok.within(drawn);
          empty = !within;
          if (within) s.setRegions(within.regions, within.paintPlan);
          else s.setRegions(() => [], () => undefined);
        } else {
          const d = (drawnPlan ?? loaded?.drawnPlan)?.(drawn);
          if (d) s.setRegions(d.regions, d.paintPlan);
          else s.setRegions(null);
        }
      } else if (ok && onTissue) s.setRegions(ok.regions, ok.paintPlan);
      else s.setRegions(null);
      setTissueOnlyState(onTissue);
      setEmptyClip(empty);
    },
    [drawnPlan],
  );

  const setArea = useCallback(
    (next: ScanArea) => {
      const s = scanRef.current;
      if (!s) return;
      // Leaving the drawn areas forgets them; drawing them wants them seen.
      if (next !== 'draw') setAreas([]);
      else setOnImage(true);
      areaRef.current = next;
      setAreaState(next);
      apply(s);
    },
    [apply, setAreas],
  );

  const setTissueOnly = useCallback(
    (on: boolean) => {
      const s = scanRef.current;
      if (!s) return;
      chosen.current = true;
      wantTissue.current = on;
      apply(s);
    },
    [apply],
  );

  const setSensitivity = useCallback((next: number) => {
    if (Number.isFinite(next)) setSensitivityState(Math.min(1, Math.max(-1, next)));
  }, []);

  const addArea = useCallback(
    (a: PlacedField) => {
      setAreas([...areasRef.current, a]);
      setOnImage(true);
      const s = scanRef.current;
      if (s && areaRef.current === 'draw') apply(s);
    },
    [apply, setAreas],
  );

  const clearAreas = useCallback(() => {
    setAreas([]);
    const s = scanRef.current;
    if (s && areaRef.current === 'draw') apply(s);
  }, [apply, setAreas]);

  // Look for the tissue as soon as the bar opens, so it is usually known by
  // the time Play is pressed, and again (once the slider rests) at each new
  // sensitivity. Tissue only goes on by itself once found; a scan already
  // under way is not replanned behind the reader's back. A new sensitivity is
  // the reader's own move: it replans, and when it finds nothing usable the
  // plan stays as it was and the popover says why.
  useEffect(() => {
    const e = forEngine.current;
    if (!scan || !e) return;
    let alive = true;
    const first = !found.current;
    if (!first && sensitivity === planned.current) {
      // Back where the plan is: nothing to find, nothing to say.
      setTissue((t) => ({ state: t.state, pieces: t.pieces }));
      return;
    }
    if (first) setTissue({ state: 'finding' });
    else setTissue((t) => ({ ...t, busy: true }));
    const find =
      findTissue ??
      ((en: ViewportEngine, uid: string, sens: number) => loadReviewScan().then((m) => m.tissuePlan(en, uid, sens)));
    const run = () =>
      find(e, seriesInstanceUID ?? '', sensitivity).then(
        (p) => {
          if (!alive) return;
          if (!first) {
            if (!p.ok) {
              const note = sensitivityNote(p.reason);
              setTissue((t) => ({ ...t, busy: false, note }));
              return;
            }
            plan.current = p;
            planned.current = sensitivity;
            setTissue({ state: 'ready', pieces: p.pieces });
            // Turned off while it was being found: the scan is not the tissue's.
            if (wantTissue.current) apply(scan);
            return;
          }
          found.current = true;
          plan.current = p;
          planned.current = sensitivity;
          if (!p.ok) {
            setTissue({ state: 'none', message: p.message });
            return;
          }
          setTissue({ state: 'ready', pieces: p.pieces });
          if (chosen.current || scan.snapshot().status === 'ready') apply(scan);
        },
        () => {
          if (!alive) return;
          if (first) setTissue({ state: 'none', message: 'Tissue detection failed, scanning the entire image' });
          else setTissue((t) => ({ ...t, busy: false, note: SENSITIVITY_NOTES.failed }));
        },
      );
    // The first look at once; a sliding sensitivity once it rests.
    const timer = first ? null : setTimeout(run, SENSITIVITY_SETTLE_MS);
    if (first) void run();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
    // The slide is fixed for the scan's life: a new slide means a new scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan, sensitivity]);

  const toggle = useCallback(() => {
    if (scanRef.current || loading) {
      close();
      return;
    }
    if (!engine || !supported) return;
    const token = ++opening.current;
    forEngine.current = engine;
    setLoading(true);
    const made = create
      ? Promise.resolve(create(engine, seenImageId))
      : loadReviewScan().then(
          (m) =>
            new m.ReviewScan(m.scanViewOf(engine), {
              coverage: seenImageId && m.canEncode() ? m.seenCoverage(seenImageId, engine) : undefined,
            }),
        );
    made.then(
      (s) => {
        if (token !== opening.current) {
          s.dispose();
          return;
        }
        s.prepare();
        scanRef.current = s;
        setScan(s);
        setLoading(false);
      },
      () => {
        if (token === opening.current) setLoading(false);
      },
    );
  }, [engine, supported, seenImageId, create, loading, close]);

  const prefetch = useCallback(() => {
    if (!create) void loadReviewScan().catch(() => undefined);
  }, [create]);

  // The slide changed (the engine is gone or another) or compare opened: the
  // scan ends, and so does one still being fetched for the old engine.
  useEffect(() => {
    if (forEngine.current && (engine !== forEngine.current || !supported)) close();
  }, [engine, supported, close]);
  useEffect(
    () => () => {
      opening.current++;
      scanRef.current?.dispose();
    },
    [],
  );

  // What the detector kept goes as the reader moves on (the user,
  // 2026-10-01): a slide's raster (~8 MB) once another slide is on screen,
  // and everything, its tissue maps too, once the page goes.
  const forget = useRef(forgetTissue);
  useEffect(() => {
    forget.current = forgetTissue;
  }, [forgetTissue]);
  useEffect(() => {
    const uid = seriesInstanceUID;
    return () => {
      if (uid) forget.current(uid);
    };
  }, [seriesInstanceUID]);
  useEffect(() => () => forget.current(), []);

  // The visited areas start off the image (the user, 2026-10-14: off when
  // entering guided review), on opening and on Restart, which is the same
  // thing, and come off it whenever the scan starts moving: Play, Space, a
  // resume (the user, 2026-10-01). Steps never play, so they leave it as it is.
  useEffect(() => {
    if (!scan) return;
    setOnImage(false);
    let was = scan.snapshot().status;
    return scan.subscribe((s) => {
      const restarted = s.status === 'ready' && was !== 'idle';
      if (s.status !== was && (s.status === 'playing' || restarted)) setOnImage(false);
      was = s.status;
    });
  }, [scan]);

  // Drawing needs a still view: an armed tool pauses, and Play waits. So
  // does a scan of drawn areas with none drawn yet.
  const blocked =
    tool !== 'none'
      ? TOOL_BLOCKS
      : area === 'draw' && areas.length === 0
        ? DRAW_BLOCKS
        : area === 'draw' && emptyClip
          ? NO_TISSUE_IN_AREAS
          : undefined;
  // Play also waits while the tissue is being found: the plan is about to change.
  const analyzing = tissue.state === 'finding';
  useEffect(() => {
    if (scan && tool !== 'none') scan.pause('tool');
  }, [scan, tool]);

  // Space plays and pauses; Escape pauses; the arrows step. None of the page's other
  // keydown handlers (Viewport: f + - digits; StudyPage: d [ ] tools) touches
  // these keys. Escape never closes the bar: the annotation overlay owns it
  // for leaving a tool, and a tool means the scan is already paused.
  useEffect(() => {
    if (!scan) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      // An element, or nothing: a key sent to the document itself has no
      // closest() to ask, and is the page's anyway.
      const t = e.target instanceof Element ? (e.target as HTMLElement) : null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
        return;
      }
      // A dialog over the viewer (Edit details) owns its keys.
      if (t?.closest('[role="dialog"], [role="alertdialog"]')) return;
      if (e.key === ' ') {
        // A focused control takes Space itself; toggling as well would act twice.
        if (spaceActivates(t)) return;
        e.preventDefault();
        const st = scan.snapshot().status;
        if (st === 'playing') scan.pause('user');
        else if (blocked || analyzing || st === 'done') return;
        else scan.play();
      } else if (e.key === 'Escape' && scan.snapshot().status === 'playing') {
        scan.pause('user');
      } else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && !e.defaultPrevented) {
        // The scrub track and the menu take their own arrows (and say so by
        // preventing the default); anywhere else they step the scan.
        if (t?.closest('[role="slider"], [role="menu"]')) return;
        if (blocked || analyzing) return;
        e.preventDefault();
        scan.step(e.key === 'ArrowRight' ? 1 : -1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [scan, blocked, analyzing]);

  const drawing = area === 'draw' && tool === 'none';
  const session = useMemo<ScanSession | null>(
    () =>
      scan
        ? {
            controller: scan,
            close,
            blocked,
            area,
            setArea,
            tissueOnly,
            setTissueOnly,
            sensitivity,
            setSensitivity,
            tissue,
            onImage,
            setOnImage,
            areas,
            addArea,
            clearAreas,
            drawing,
          }
        : null,
    [
      scan,
      close,
      blocked,
      area,
      setArea,
      tissueOnly,
      setTissueOnly,
      sensitivity,
      setSensitivity,
      tissue,
      onImage,
      setOnImage,
      areas,
      addArea,
      clearAreas,
      drawing,
    ],
  );

  return { session, pending: loading && !scan, open: !!scan || loading, loading, toggle, close, prefetch };
}
